import React, { useEffect } from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

type ReleaseNote = {
  date: string;
  title: string;
  summary: string;
  tags: string[];
  highlights: string[];
};

const releaseNotes: ReleaseNote[] = [
  {
    date: "August 13, 2026",
    title: "Schedule a custom workflow from ticket view",
    summary:
      "Technicians can start a custom workflow now or schedule it for a specific time. Chat stays available until the workflow starts.",
    tags: ["Workflows", "Scheduler"],
    highlights: [
      "Select Workflow on a ticket still runs immediately when you click the workflow name.",
      "A clock action schedules that custom workflow for a datetime and timezone. The Run at field defaults to now. Expand each step in that dialog to change its delay for this run only; the saved workflow is not updated. Pending starts do not lock ticket chat.",
      "When the time arrives, Theo starts the workflow on the ticket thread, or creates a thread if the ticket does not have one.",
      "Step delays inside a workflow are unchanged: they still pause and resume after the workflow has started.",
    ],
  },
  {
    date: "July 20, 2026",
    title: "Ticket Lifecycle, Auto Mode, and deeper integrations",
    summary:
      "Theo adds stale-ticket lifecycle tools, configurable Auto Mode automation, richer M365 and Google Workspace admin actions, Datto fleet tools, and Sherweb marketplace support.",
    tags: ["Ticket Lifecycle", "Auto Mode", "M365", "RMM", "Sherweb"],
    highlights: [
      "Ticket Lifecycle helps MSPs find and act on stale Halo tickets with executive/technician overviews, snooze, reply drafts, and AI insights.",
      "Auto Mode and Playbooks let you configure Auto Resolver and Suggestions per customer, plus reusable playbooks for common workflows.",
      "Usage Insights shows Theo coverage and estimated time saved by Halo category, with ticket drilldowns.",
      "New M365 capabilities: Identity Protection risky users, richer MFA phone methods, quarantine preview/delete, mailbox storage analysis, transport/journal rules, connectors, and app registrations.",
      "Datto RMM: hostname and fleet metadata lookups, device status, and secure shell for endpoint investigation.",
      "Sherweb marketplace integration for customer catalog, orders, subscriptions, and billing charges.",
      "Google Shared Drives management plus expanded Vault and offboarding actions.",
      "Compose Email, CSV export, and custom workflow list/start from chat.",
    ],
  },
  {
    date: "May 4, 2026",
    title: "MSP Dashboard",
    summary: "Theo now opens to a clearer MSP Dashboard for reviewing automation opportunities and recent report activity.",
    tags: ["Dashboard", "Reports"],
    highlights: [
      "Saved reports are easier to scan, open, and share.",
      "Report filters now show clearer loading feedback.",
      "Theo includes automatable ticket examples directly in generated reports.",
    ],
  },
  {
    date: "April 27, 2026",
    title: "Activity and visibility",
    summary: "Theo has better visibility into AI activity, usage, and ticket conversations across your MSP.",
    tags: ["Activity", "Observer"],
    highlights: [
      "Activity reports now make it easier to review actions and audit logs.",
      "AI thread messages render more consistently with the main chat experience.",
      "MSP analytics can be filtered by queue and technician.",
    ],
  },
  {
    date: "April 20, 2026",
    title: "Richer ticket insights",
    summary: "Theo's ticket analysis now gives more useful context about ticket patterns and automation readiness.",
    tags: ["Tickets", "Insights"],
    highlights: [
      "Reports include ticket landscape, time-saved, and sample-ticket details.",
      "Large report tables export more reliably to PDF.",
      "Workflow pages are easier to navigate and share.",
    ],
  },
  {
    date: "April 13, 2026",
    title: "Setup guidance",
    summary: "Theo is easier to set up with expanded Help Center guidance and clearer integration checks.",
    tags: ["Help Center", "Onboarding", "Integrations"],
    highlights: [
      "New Help Center guides cover setup and common workflows.",
      "Integration checks now give clearer feedback when permissions need attention.",
      "Ticket ingestion status is easier to review from Observer.",
    ],
  },
  {
    date: "April 6, 2026",
    title: "Faster ticket lookup",
    summary: "Theo can find and open relevant ticket context more smoothly from supported PSA workflows.",
    tags: ["Extension", "Search", "Tickets"],
    highlights: [
      "Autotask ticket search helps technicians find tickets faster.",
      "Browser extension navigation is more reliable across tabs.",
      "Support portal workflows are better supported.",
    ],
  },
  {
    date: "March 23, 2026",
    title: "Custom workflows",
    summary: "Theo's workflow area now supports more reusable operational playbooks for technician tasks.",
    tags: ["Workflows"],
    highlights: [
      "Custom workflow pages are easier to access and manage.",
      "Customer-specific workflow links preserve the right context.",
      "Workflow editing is clearer and more predictable.",
    ],
  },
  {
    date: "March 9, 2026",
    title: "Operational reporting",
    summary: "Theo's reporting views now provide a cleaner foundation for reviewing MSP activity and automation opportunities.",
    tags: ["Reporting", "Observer"],
    highlights: [
      "Reporting cards and filters are easier to read.",
      "Observer views include more useful thread and ticket context.",
      "MSP setup flows are smoother for administrators.",
    ],
  },
  {
    date: "February 23, 2026",
    title: "Simpler navigation",
    summary: "Theo's main navigation is cleaner, making it easier to move between analytics, AI, activity, and help.",
    tags: ["Navigation", "UX"],
    highlights: [
      "Sidebar sections are grouped by how teams use Theo.",
      "Page titles are more consistent across the app.",
      "Setup and configuration entry points are easier to find.",
    ],
  },
  {
    date: "February 9, 2026",
    title: "Better assistant handoffs",
    summary: "Theo gives technicians more helpful context when moving from chat into tickets, reports, and customer work.",
    tags: ["Chat", "Tickets"],
    highlights: [
      "Ticket links open with clearer context for the next action.",
      "Assistant messages are easier to scan in longer conversations.",
      "Customer and ticket references are more consistent across Theo views.",
    ],
  },
];

const ReleaseNotes: React.FC = () => {
  useEffect(() => {
    document.title = "Release Notes - Theo";
    return () => {
      document.title = "Theo";
    };
  }, []);

  return (
    <Box sx={{ width: "100%", minHeight: "100%", bgcolor: "#f8fafc", pb: 7 }}>
      <Box sx={{ maxWidth: 1040, mx: "auto", px: { xs: 2, sm: 3 }, pt: { xs: 3, md: 5 } }}>
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e8e4df",
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 3,
            boxShadow: "0 18px 50px rgba(15,23,42,0.06)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                bgcolor: "rgba(112,93,74,0.10)",
                color: "#705d4a",
                display: "grid",
                placeItems: "center",
              }}
            >
              <AutoAwesomeRoundedIcon />
            </Box>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 800, fontSize: { xs: 30, md: 38 }, color: "#0f172a" }}>
              Release Notes
            </Typography>
          </Stack>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 16, lineHeight: 1.7, color: "#64748b", maxWidth: 760 }}>
            Updates to Theo AI agent.
          </Typography>
        </Box>

        <Stack spacing={2.5}>
          {releaseNotes.map((release, index) => (
            <Card
              key={release.date}
              elevation={0}
              sx={{
                border: "1px solid #e8e4df",
                borderRadius: 3,
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, fontWeight: 700, color: "#705d4a", mb: 0.5 }}>
                      {release.date}
                    </Typography>
                    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: { xs: 21, md: 24 }, fontWeight: 800, color: "#0f172a" }}>
                      {release.title}
                    </Typography>
                  </Box>
                  {index === 0 && (
                    <Chip
                      label="Latest"
                      size="small"
                      sx={{ alignSelf: { xs: "flex-start", sm: "center" }, bgcolor: "rgba(112,93,74,0.10)", color: "#705d4a", fontWeight: 700 }}
                    />
                  )}
                </Stack>

                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 15, color: "#64748b", lineHeight: 1.65, mb: 2 }}>
                  {release.summary}
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                  {release.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ borderColor: "#d8d0c7", color: "#705d4a" }} />
                  ))}
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
                  {release.highlights.map((highlight) => (
                    <Typography
                      key={highlight}
                      component="li"
                      sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14.5, color: "#334155", lineHeight: 1.6 }}
                    >
                      {highlight}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Card>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default ReleaseNotes;
